'use client';

import { useQuery } from '@tanstack/react-query';
import ErrorDisplay from 'components/common/ErrorDisplay';
import Loader from 'components/common/Loader';
import {
  useAddressAllowances,
  useAddressEvents,
  useAddressPageContext,
} from 'lib/hooks/page-context/AddressPageContext';
import { isNullish } from 'lib/utils';
import { getAllowanceKey } from 'lib/utils/allowances';
import analytics from 'lib/utils/analytics';
import { getEventKey } from 'lib/utils/events';
import { type Exploit, getExploitStatus } from 'lib/utils/exploits';
import ExploitStatus from './ExploitStatus';

interface Props {
  exploit: Exploit;
}

const ExploitChecker = ({ exploit }: Props) => {
  const { address, selectedChainId } = useAddressPageContext();
  const { allowances, error, isLoading } = useAddressAllowances();
  const { events } = useAddressEvents();

  // We wrap the status getting in a useQuery mainly so that we can make sure the analytics tracking is handled correctly
  const { data: status } = useQuery({
    queryKey: ['exploit-status', exploit.slug, allowances?.map(getAllowanceKey), events?.map(getEventKey)],
    queryFn: () => {
      const status = getExploitStatus(events!, allowances!, exploit);
      analytics.track('Exploit Checked', { exploit: exploit.slug, account: address, chainId: selectedChainId, status });
      return status;
    },
    enabled: !isNullish(address) && !isNullish(events) && !isNullish(allowances) && !isNullish(selectedChainId),
  });

  if (!address || !selectedChainId) return null;

  return (
    <Loader isLoading={isLoading} className="exploit-checker-loader">
      {/* We use "safe" status as default for formatting the loader */}
      {error ? (
        <div className="p-4 rounded-lg border border-black dark:border-white">
          <ErrorDisplay error={error} />
        </div>
      ) : (
        <ExploitStatus
          status={status ?? 'safe'}
          allowances={allowances ?? []}
          exploit={exploit}
          address={address}
          chainId={selectedChainId}
        />
      )}
    </Loader>
  );
};

export default ExploitChecker;
